import React, { useEffect } from 'react';

export interface BreadcrumbItem {
  name: string;
  url: string;
}

export interface SEOHeadProps {
  title: string;
  description: string;
  canonicalPath?: string;
  ogImage?: string;
  ogType?: 'website' | 'article' | 'product';
  publishedTime?: string;
  keywords?: string;
  geoRegion?: string;
  geoPlacename?: string;
  geoPosition?: string;
  icbm?: string;
  breadcrumbs?: BreadcrumbItem[];
  schema?: Record<string, any> | Array<Record<string, any>>;
  robots?: string;
}

export const SEOHead: React.FC<SEOHeadProps> = ({
  title,
  description,
  canonicalPath = '/',
  ogImage = 'https://www.scalebulk.com/og-image.jpg',
  ogType = 'website',
  publishedTime,
  keywords,
  geoRegion,
  geoPlacename,
  geoPosition,
  icbm,
  breadcrumbs,
  schema,
  robots = 'index, follow, max-snippet:-1, max-image-preview:large, max-video-preview:-1'
}) => {
  const fullTitle = title.includes('Scale Bulk') ? title : `${title} | Scale Bulk`;
  const canonicalUrl = `https://www.scalebulk.com${canonicalPath.startsWith('/') ? canonicalPath : `/${canonicalPath}`}`;

  useEffect(() => {
    // 1. Update Document Title
    document.title = fullTitle;

    // 2. Helper to set or create meta tag
    const setMetaTag = (nameAttr: 'name' | 'property', nameVal: string, content: string) => {
      let element = document.querySelector(`meta[${nameAttr}="${nameVal}"]`);
      if (!element) {
        element = document.createElement('meta');
        element.setAttribute(nameAttr, nameVal);
        document.head.appendChild(element);
      }
      element.setAttribute('content', content);
    };

    // Description & Standard SEO
    setMetaTag('name', 'description', description);
    setMetaTag('name', 'robots', robots);
    setMetaTag('name', 'author', 'Scale Bulk');
    setMetaTag('name', 'publisher', 'Scale Bulk');
    setMetaTag('name', 'rating', 'General');
    setMetaTag('name', 'distribution', 'Global');

    // Keywords (SEO/AEO indexation signal)
    if (keywords) {
      setMetaTag('name', 'keywords', keywords);
    }

    // GEO Tags (Local / Regional Multi-Timezone targeting)
    if (geoRegion) {
      setMetaTag('name', 'geo.region', geoRegion);
    }
    if (geoPlacename) {
      setMetaTag('name', 'geo.placename', geoPlacename);
    }
    if (geoPosition) {
      setMetaTag('name', 'geo.position', geoPosition);
      setMetaTag('name', 'ICBM', icbm || geoPosition.replace(';', ', '));
    } else if (icbm) {
      setMetaTag('name', 'ICBM', icbm);
    }

    // Open Graph
    setMetaTag('property', 'og:title', fullTitle);
    setMetaTag('property', 'og:description', description);
    setMetaTag('property', 'og:url', canonicalUrl);
    setMetaTag('property', 'og:type', ogType);
    setMetaTag('property', 'og:image', ogImage);
    setMetaTag('property', 'og:site_name', 'Scale Bulk');

    if (publishedTime) {
      setMetaTag('property', 'article:published_time', publishedTime);
    }

    // Twitter / X Card
    setMetaTag('name', 'twitter:card', 'summary_large_image');
    setMetaTag('name', 'twitter:site', '@scalebulk');
    setMetaTag('name', 'twitter:creator', '@scalebulk');
    setMetaTag('name', 'twitter:title', fullTitle);
    setMetaTag('name', 'twitter:description', description);
    setMetaTag('name', 'twitter:image', ogImage);

    // Canonical Link
    let canonicalLink = document.querySelector('link[rel="canonical"]');
    if (!canonicalLink) {
      canonicalLink = document.createElement('link');
      canonicalLink.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalLink);
    }
    canonicalLink.setAttribute('href', canonicalUrl);

    // Google Search Console Site Verification
    const googleSiteVerification = (import.meta as any).env?.VITE_GOOGLE_SITE_VERIFICATION;
    if (googleSiteVerification) {
      setMetaTag('name', 'google-site-verification', googleSiteVerification);
    }

    // Google Analytics 4
    const gaMeasurementId = (import.meta as any).env?.VITE_GA_MEASUREMENT_ID;
    if (gaMeasurementId && !document.getElementById('ga4-script')) {
      const gaScript = document.createElement('script');
      gaScript.id = 'ga4-script';
      gaScript.async = true;
      gaScript.src = `https://www.googletagmanager.com/gtag/js?id=${gaMeasurementId}`;
      document.head.appendChild(gaScript);

      const gaInitScript = document.createElement('script');
      gaInitScript.id = 'ga4-init';
      gaInitScript.text = `
        window.dataLayer = window.dataLayer || [];
        function gtag(){dataLayer.push(arguments);}
        gtag('js', new Date());
        gtag('config', '${gaMeasurementId}', { page_path: window.location.pathname });
      `;
      document.head.appendChild(gaInitScript);
    }

    // JSON-LD Structured Data Injection
    const existingSchemaScript = document.getElementById('jsonld-structured-data');
    if (existingSchemaScript) {
      existingSchemaScript.remove();
    }

    // Build final schema payload
    let finalSchema: any = schema;

    if (breadcrumbs && breadcrumbs.length > 0) {
      const breadcrumbSchema = {
        '@type': 'BreadcrumbList',
        'itemListElement': breadcrumbs.map((crumb, idx) => ({
          '@type': 'ListItem',
          'position': idx + 1,
          'name': crumb.name,
          'item': crumb.url.startsWith('http')
            ? crumb.url
            : `https://www.scalebulk.com${crumb.url.startsWith('/') ? crumb.url : `/${crumb.url}`}`
        }))
      };

      if (!finalSchema) {
        finalSchema = {
          '@context': 'https://schema.org',
          ...breadcrumbSchema
        };
      } else if (finalSchema['@graph'] && Array.isArray(finalSchema['@graph'])) {
        finalSchema = {
          ...finalSchema,
          '@graph': [...finalSchema['@graph'], breadcrumbSchema]
        };
      } else if (Array.isArray(finalSchema)) {
        finalSchema = [...finalSchema, breadcrumbSchema];
      } else {
        finalSchema = {
          '@context': 'https://schema.org',
          '@graph': [
            finalSchema['@context'] ? finalSchema : { '@context': 'https://schema.org', ...finalSchema },
            breadcrumbSchema
          ]
        };
      }
    }

    if (finalSchema) {
      const script = document.createElement('script');
      script.id = 'jsonld-structured-data';
      script.type = 'application/ld+json';
      script.text = JSON.stringify(finalSchema);
      document.head.appendChild(script);
    }
  }, [fullTitle, description, canonicalUrl, ogImage, ogType, publishedTime, keywords, geoRegion, geoPlacename, geoPosition, icbm, breadcrumbs, schema]);

  return null;
};
